import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useConnectionStore } from '@/stores/connectionStore';
import { Settings, RefreshCw, ChevronDown, CheckCircle, XCircle, Clock, Circle } from 'lucide-react';

type ChangeStatus = 'scheduled' | 'running' | 'success' | 'failed';

interface Change {
  type: 'create' | 'update' | 'delete';
  description: string;
}

interface ChangeGroup {
  id: string;
  timestamp: string;
  goal: string;
  status: ChangeStatus;
  changes: Change[];
}

const STATUS_CONFIG: Record<ChangeStatus, { icon: typeof CheckCircle; color: string; label: string }> = {
  scheduled: { icon: Clock, color: 'text-yellow-400', label: 'Scheduled' },
  running: { icon: Circle, color: 'text-blue-400 animate-pulse', label: 'Running' },
  success: { icon: CheckCircle, color: 'text-green-400', label: 'Success' },
  failed: { icon: XCircle, color: 'text-red-400', label: 'Failed' },
};

const MOCK_CHANGELOG: ChangeGroup[] = [
  {
    id: '1',
    timestamp: '2024-02-10T14:00:00Z',
    goal: 'Enable AI Companion for Support team',
    status: 'scheduled',
    changes: [
      { type: 'update', description: 'Enable AI Companion for Support queue' },
      { type: 'update', description: 'Configure call summary settings' },
    ],
  },
  {
    id: '2',
    timestamp: '2024-02-09T10:30:00Z',
    goal: 'Add Spanish language support to Sales queue',
    status: 'success',
    changes: [
      { type: 'create', description: 'Created skill "Spanish"' },
      { type: 'update', description: 'Queue "Sales": Routing changed to Skills-based' },
      { type: 'update', description: 'Queue "Sales": Added Spanish skill requirement' },
    ],
  },
  {
    id: '3',
    timestamp: '2024-02-09T09:15:00Z',
    goal: 'Create dedicated Tier 2 support queue',
    status: 'success',
    changes: [
      { type: 'create', description: 'Created queue "Support Tier 2"' },
      { type: 'update', description: 'Queue "Support Tier 2": Set routing to Skills-based' },
      { type: 'update', description: 'Queue "Support Tier 1": Max wait time 300s → 180s' },
    ],
  },
  {
    id: '4',
    timestamp: '2024-02-08T16:30:00Z',
    goal: 'Upgrade John Doe to Pro license',
    status: 'failed',
    changes: [
      { type: 'update', description: 'User john.doe@company.com: License Basic → Pro' },
    ],
  },
  {
    id: '5',
    timestamp: '2024-02-08T11:00:00Z',
    goal: 'Clean up unused Team Chat channels',
    status: 'success',
    changes: [
      { type: 'delete', description: 'Deleted channel "old-project"' },
      { type: 'delete', description: 'Deleted channel "2023-hackathon"' },
    ],
  },
];

export function PlansPage() {
  const navigate = useNavigate();
  const { isConnected } = useConnectionStore();
  const [isLoading, setIsLoading] = useState(true);
  const [changelog, setChangelog] = useState<ChangeGroup[]>([]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      await new Promise(resolve => setTimeout(resolve, 300));
      setChangelog(MOCK_CHANGELOG);
      // Expand the first item by default
      const first = MOCK_CHANGELOG[0];
      if (first) {
        setExpanded(new Set([first.id]));
      }
      setIsLoading(false);
    };

    if (isConnected) {
      load();
    } else {
      setIsLoading(false);
    }
  }, [isConnected]);

  const toggleExpanded = (id: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday = date.toDateString() === yesterday.toDateString();

    const time = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    if (isToday) return `Today ${time}`;
    if (isYesterday) return `Yesterday ${time}`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ` ${time}`;
  };

  const getTypeIndicator = (type: Change['type']) => {
    switch (type) {
      case 'create': return <span className="text-green-400">+</span>;
      case 'update': return <span className="text-blue-400">~</span>;
      case 'delete': return <span className="text-red-400">-</span>;
    }
  };

  if (!isConnected) {
    return (
      <div className="flex h-screen flex-col bg-background text-foreground">
        <main className="flex flex-1 items-center justify-center">
          <div className="text-center max-w-md">
            <Settings className="mx-auto h-12 w-12 text-muted-foreground" />
            <h2 className="mt-4 text-xl font-semibold">Connect Your Zoom Account</h2>
            <p className="mt-2 text-muted-foreground">
              Connect your Zoom account to view configuration changes.
            </p>
            <button
              onClick={() => navigate('/chat')}
              className="mt-4 rounded-md bg-primary px-4 py-2 text-primary-foreground hover:bg-primary/90"
            >
              Connect in Chat
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col bg-background text-foreground">
      <main className="flex-1 overflow-auto">
        <div className="mx-auto max-w-2xl p-6">
          <h1 className="text-lg font-medium text-foreground mb-4">Change</h1>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : changelog.length === 0 ? (
            <p className="text-muted-foreground text-sm">No changes yet.</p>
          ) : (
            <div className="space-y-2">
              {changelog.map((group) => (
                <div key={group.id} className="border border-border rounded-lg overflow-hidden">
                  <button
                    onClick={() => toggleExpanded(group.id)}
                    className="w-full flex items-center gap-3 p-3 text-left hover:bg-secondary/50 transition-colors"
                  >
                    <ChevronDown
                      className={`h-4 w-4 text-muted-foreground transition-transform ${
                        expanded.has(group.id) ? '' : '-rotate-90'
                      }`}
                    />
                    {(() => {
                      const config = STATUS_CONFIG[group.status];
                      const Icon = config.icon;
                      return <Icon className={`h-4 w-4 ${config.color}`} />;
                    })()}
                    <div className="flex-1 min-w-0">
                      <span className="text-sm text-foreground">{group.goal}</span>
                    </div>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">
                      {formatTime(group.timestamp)}
                    </span>
                  </button>

                  {expanded.has(group.id) && (
                    <div className="border-t border-border bg-secondary/20 px-3 py-2">
                      {group.changes.map((change, idx) => (
                        <div key={idx} className="flex items-center gap-2 py-1 text-xs">
                          <span className="w-3 text-center font-mono">{getTypeIndicator(change.type)}</span>
                          <span className="text-muted-foreground">{change.description}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
